<template>
    <div>
        <div v-if="estimate && estimate.locked"
             class="w-full bg-yellow-50 text-center text-sm border-b border-yellow-200 px-2 py-2 mb-0">
            <i class="fas fa-eye text-yellow-500 mr-4"></i>
            <span class="text-yellow-600"><strong>Estimate is read-only</strong> - No more changes may be made to this estimate</span>
            <div class="mt-2">
                <ui-button-new :disabled="false" @click.prevent="duplicate()" small warning>
                    Duplicate this estimate as a new revision
                    <i class="flex-shrink-0 ml-2 fas fa-copy"></i>
                </ui-button-new>
            </div>
        </div>

        <div class="mx-auto w-full max-w-screen-2xl pt-0 pb-40 px-8">

            <!-- Read-only banner. -->
            <div class="flex space-x-8" v-if="estimate" :key="estimateKey">
                <div class="flex-1">
                    <div class="sticky top-0 bg-white -mx-2 pt-4 px-2 mb-2 z-50">
                        <div class="flex flex-1">
                            <div class="flex-1">
                                <!-- Estimate Title. -->
                                <input
                                    class="text-2xl font-bold text-gray-900 border-transparent rounded shadow-none px-2 py-1 -ml-2.5 mb-0"
                                    type="text"
                                    :disabled="estimate.readonly"
                                    v-model="estimate.title"
                                    @input="estimate.update()"
                                    @change="estimate.addSnapshot()"
                                    :placeholder="`Estimate...`"
                                    @keydown.enter.prevent="$emit('change', $event.target.value);$event.target.blur()"
                                >
                                <!-- Estimate Metadata. -->
                                <div class="text-sm text-gray-500">
                                    <!--
                                    <span>
                                        <i class="fa fa-pencil-alt text-gray-400 mr-1"></i>
                                        <span class="text-gray-500">Draft</span>
                                    </span>
                                    • -->Reference {{ estimate.package_reference }} - {{ estimate.reference }}
                                    <span v-if="estimate.revision">• Revision {{ estimate.revision || 0 }}</span>
                                </div>
                            </div>
                            <div class="pt-3">
                                <div @click="showSummary"
                                     class="inline-block m-1 border-0 shadow-none px-3 py-2 font-medium text-sm rounded-md focus:z-20 focus:outline-none "
                                     style="min-width: 20px;cursor: pointer"
                                     :class="mode==='summary'?'bg-blue-100 text-blue-700 focus:ring-2 focus:ring-blue-600':''"
                                >Summary
                                </div>
                                <div @click="showScopeOfWorks"
                                     class="inline-block m-1 border-0 shadow-none px-3 py-2 font-medium text-sm rounded-md focus:z-20 focus:outline-none "
                                     style="min-width: 20px;cursor: pointer"
                                     :class="mode==='scopeOfWorks'?'bg-blue-100 text-blue-700 focus:ring-2 focus:ring-blue-600':''"
                                >Scope of Works
                                </div>
                                <div @click="showPreliminaries"
                                     class="inline-block m-1 border-0 shadow-none px-3 py-2 font-medium text-sm rounded-md focus:z-20 focus:outline-none "
                                     style="min-width: 20px;cursor: pointer"
                                     :class="mode==='preliminaries'?'bg-blue-100 text-blue-700 focus:ring-2 focus:ring-blue-600':''"
                                >Preliminaries
                                </div>
                                <div @click="showDayworks"
                                     class="inline-block m-1 border-0 shadow-none px-3 py-2 font-medium text-sm rounded-md focus:z-20 focus:outline-none "
                                     style="min-width: 20px;cursor: pointer"
                                     :class="mode==='dayworks'?'bg-blue-100 text-blue-700 focus:ring-2 focus:ring-blue-600':''"
                                >Daywork Rates
                                </div>
                                <div @click="showAttendances"
                                     class="inline-block m-1 border-0 shadow-none px-3 py-2 font-medium text-sm rounded-md focus:z-20 focus:outline-none "
                                     style="min-width: 20px;cursor: pointer"
                                     :class="mode==='attendances'?'bg-blue-100 text-blue-700 focus:ring-2 focus:ring-blue-600':''"
                                >Attendances
                                </div>
                            </div>
                        </div>

                        <!-- Estimate Tabs. -->
                        <estimate-tabs
                            class="mt-8"
                            :estimate="estimate"
                            @update:active-section-index="updateSectionIndex"
                            :section-index="activeSectionIndex"
                            :active-page.sync="activePage"
                            :default-scroll-offset-data="scrollOffsetData"
                            @updating="(e) => this.scrollOffsetData = e"
                            :key="userKey"
                            v-if="mode==='scopeOfWorks'"
                        ></estimate-tabs>

                        <!-- Estimate section header (if applicable). -->
                        <div v-if="(mode==='scopeOfWorks') && (activeSectionIndex !== null)"
                             class="flex mt-8 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                            <div class="w-6 flex-shrink-0"></div>
                            <div class="w-16 border-l border-r border-gray-200 px-2 py-1">Ref</div>
                            <div class="flex-1 border-r border-gray-200 px-2 py-1">Description</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Quantity</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Uom</div>
                            <div class="w-28 -ml-px border-r border-gray-200 px-2 py-1">Rate</div>
                            <div class="w-28 -ml-px border-r border-gray-200 px-2 py-1">Total</div>
                            <div class="w-6 flex-shrink-0"></div>
                            <div class="w-6 flex-shrink-0"></div>
                        </div>
                        <!-- Estimate section header (if applicable). -->
                        <div v-if="(mode==='preliminaries')"
                             class="flex mt-8 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                            <div class="w-6 flex-shrink-0"></div>
                            <div class="w-16 border-l border-r border-gray-200 px-2 py-1">Ref</div>
                            <div class="flex-1 border-r border-gray-200 px-2 py-1">Description</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Quantity</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Uom</div>
                            <div class="w-28 -ml-px border-r border-gray-200 px-2 py-1">Rate</div>
                            <div class="w-28 -ml-px border-r border-gray-200 px-2 py-1">Total</div>
                            <div class="w-6 flex-shrink-0"></div>
                        </div>
                        <!-- Estimate Dayworks header (if applicable). -->
                        <div v-if="(mode==='dayworks')"
                             class="flex mt-8 border bg-gray-50 border-gray-200 rounded-xl text-sm text-gray-500 font-semibold">
                            <div class="p-3 flex">
                                <label class="px-2 mt-0.5 pt-px">Material markup percent:</label>
                                <ui-input :disabled="estimate.readonly" v-model="materialMarkup" class="w-32"
                                          @change="estimate.materialMarkup = parseFloat(materialMarkup); estimate.update()"
                                          @blur="materialMarkup = parseFloat(materialMarkup)+'%'"
                                          field="py-0.5 text-right"
                                          placeholder="%"></ui-input>

                            </div>
                        </div>
                        <div v-if="(mode==='dayworks')"
                             class="flex mt-4 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                            <div class="w-6 flex-shrink-0"></div>
                            <div class="flex-1 border-r border-gray-200 px-2 py-1">Description</div>
                            <div class="w-20 border-r border-gray-200 px-2 py-1">Unit</div>
                            <div class="w-28 border-r border-gray-200 px-2 py-1">Rate</div>
                            <div class="w-6 flex-shrink-0"></div>
                        </div>
                        <!-- Attendances section header (if applicable). -->
                        <div v-if="(mode==='attendances')"
                             class="flex mt-8 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                            <div class="w-6 flex-shrink-0 border-r"></div>
                            <div class="flex-1 border-r border-gray-200 px-2 py-1">Description</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Others</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Us</div>
                            <div class="flex-1 -ml-px border-r border-gray-200 px-2 py-1">Comment</div>
                            <div class="w-6 flex-shrink-0"></div>
                        </div>

                    </div>
                    <estimate-section v-if="mode==='scopeOfWorks'"
                                      ref="section"
                                      :key="userKey"
                                      :estimate="estimate"
                                      @ping="ping" :pinging="pinging"
                                      :sectionIndex="activeSectionIndex"
                                      :clipboard-key="clipboardKey"
                                      :clipboard-item-data="clipboardItemData"
                                      :clipboard-task-data="clipboardTaskData"
                                      :multimode="multimode"
                                      :store="store"></estimate-section>
                    <!-- Preliminaries -->
                    <estimate-preliminaries v-if="mode==='preliminaries'"
                                            ref="preliminaries"
                                            :estimate="estimate"
                                            :clipboard-key="clipboardKey"
                                            :clipboard-group-item-data="clipboardGroupItemData"
                                            :clipboard-group-data="clipboardGroupData"
                                            :multimode="multimode"
                                            :store="store"></estimate-preliminaries>
                    <!-- Daywork Rates -->
                    <estimate-daywork-rates v-if="mode==='dayworks'"
                                            ref="dayworks"
                                            :estimate="estimate"
                                            :store="store"></estimate-daywork-rates>
                    <!-- Summary. -->
                    <ui-form-section v-else-if="mode==='summary'" class="mt-8">
                        <div class="col-span-full xl:col-span-6">
                            <label class="block text-sm font-medium text-gray-700 mb-1" v-text="'To'"></label>
                            <package-contact-search
                                class="flex-1"
                                #default="{ open, query, setQuery }"
                                @selected:contact="summaryToSelected"
                                :top="true"
                                :contacts="contacts"
                            >
                                <input
                                    v-model="estimate.summaryTo"
                                    :disabled="estimate.readonly"
                                    :type="'text'"
                                    class="m-0 mt-1 p-2 relative rounded-md shadow-sm block w-full border sm:text-sm focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 border-gray-300 placeholder-gray-400 form-control input"
                                    :class="estimate.readonly?'bg-gray-50':''"
                                    placeholder="Persons name..."
                                    @input="setQuery($event.target.value);estimate.update();"
                                    @focus="setQuery($event.target.value);open()"
                                    @change="estimate.addSnapshot()"
                                />
                            </package-contact-search>
                        </div>
                        <ui-date-picker label="Date" name="summaryDate"
                                        v-model="estimate.summaryDate"
                                        :disabled="estimate.readonly"
                                        @input="estimate.update()"
                                        @change="estimate.addSnapshot()"
                                        class="xl:col-span-6"></ui-date-picker>
                        <ui-textarea autoresize label="Client Address" name="summaryAddress"
                                     :disabled="estimate.readonly"
                                     v-model="estimate.summaryAddress"
                                     @input="estimate.update()"
                                     @change="estimate.addSnapshot()"
                                     class="xl:col-span-6"></ui-textarea>
                        <ui-date-picker label="Valid Until" name="summaryValidUntil"
                                        :disabled="estimate.readonly"
                                        v-model="estimate.summaryValidUntil"
                                        @input="estimate.update()"
                                        @change="estimate.addSnapshot()"
                                        class="xl:col-span-6"></ui-date-picker>
                        <estimate-summary-fields class="col-span-full"
                                                 :estimate="estimate"></estimate-summary-fields>
                    </ui-form-section>
                    <!-- Attendances. -->
                    <div v-else-if="mode==='attendances'">
                        <!-- Attendance header (if applicable). -->
                        <draggable :list="estimate.attendances" draggable=".draggable" handle=".handle"
                                   group="attendances"
                                   @change="reorder()">
                            <estimate-attendance :estimate="estimate" v-for="(attendance,index) in estimate.attendances"
                                                 :key="'attendance'+index" :attendance="attendance"
                                                 ref="attendance"></estimate-attendance>
                            <!-- New Task Item. -->
                            <div class="flex" v-if="!estimate.readonly">
                                <div class="w-6 flex-shrink-0 self-start py-1.5 -my-px border-t">
                                    <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
                                </div>
                                <div class="flex-1 ">
                                    <input
                                        placeholder="Add a new attendance..."
                                        class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-300 shadow-none relative hover:z-10 focus:z-30 resize-none bg-white"
                                        type="text"
                                        v-model="newAttendance.description"
                                        @input="addAttendance()"
                                    >
                                </div>
                                <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1"></div>
                            </div>
                        </draggable>
                    </div>
                </div>
                <div class="w-72 space-y-8 relative pt-4">
                    <div class="w-72 space-y-1 fixed">
                        <estimate-action-panel :estimate="estimate"
                                               :user="user"
                                               @deleted="(e) => $emit('deleted',e)"
                                               @print="print"
                                               @duplicate="duplicate"
                                               @reflow="$refs.section?$refs.section.resizeElements():false"
                        ></estimate-action-panel>
                        <div
                            style="overflow-y: auto; padding-right:12px; margin-top:12px; padding-top:12px; top:0px;"
                            :style="estimate && estimate.readonly?'height:calc(100vh - 320px)':'height:calc(100vh - 250px)'">
                            <estimate-status-panel :estimate="estimate"
                                                   @toggleEditMode="toggleEditMode"></estimate-status-panel>
                            <estimate-settings-panel :estimate="estimate"></estimate-settings-panel>
                            <estimate-collection-panel :estimate="estimate"></estimate-collection-panel>
                            <estimate-assemblies-panel :estimate="estimate" :store="store" :pinging="pinging"
                                                       @ping="ping"></estimate-assemblies-panel>
                            <estimate-metadata-panel :estimate="estimate"></estimate-metadata-panel>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex space-x-8 pt-4" v-else-if="this.id">
                Loading Estimate....
            </div>
            <div class="flex space-x-8 pt-4" v-else>
                Please select an estimate to edit or click new to create a new estimate.
            </div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import {Contact, Estimate, EstimateLabourRate, MaterialNew, PackageNew as Package, SupplierQuote} from '@models'
import EstimateStatusPanel from "./EstimateStatusPanel.vue"
import moment from "moment"
import PackageContactSearch from "../../contacts/PackageContactSearch.vue"
import User from "../../../services/User"
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import remoteStorage from "../../../utils/remoteStorage";
import EstimateTask from "./EstimateTask.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import EstimateAttendance from "./EstimateAttendance.vue";
import EstimateSummaryFields from "./EstimateSummaryFields.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiTextarea from "../../ui/UiTextarea.vue";
import EstimateSection from "./EstimateSection.vue";
import UiFormSection from "../../ui/UiFormSection.vue";
import EstimateTabs from "./EstimateTabs.vue";
import EstimatePreliminaries from "./EstimatePreliminaries.vue";
import UiInput from "../../ui/UiInput.vue";
import EstimateDayworkRates from "./EstimateDayworkRates.vue";

export default {
    components: {
        EstimateDayworkRates,
        UiInput,
        EstimatePreliminaries,
        EstimateTabs,
        UiFormSection,
        EstimateSection,
        UiTextarea,
        UiDatePicker,
        EstimateSummaryFields,
        EstimateAttendance,
        UiButtonNew,
        EstimateTask, UiDropdownItem, UiDropdownDivider, draggable, PackageContactSearch, EstimateStatusPanel
    },
    props: {
        id: null | String,
        selectedItem: Object,
        user: Object,
    },
    data() {
        return {
            estimate: null,
            activeSectionIndex: null,
            activePage: 'summary',
            iframe: false,
            userKey: 1,
            clipboardKey: 1,
            clipboardTaskData: remoteStorage.getItem('estimate-task-clipboard', [], true),
            clipboardItemData: remoteStorage.getItem('estimate-item-clipboard', [], true),
            clipboardGroupData: remoteStorage.getItem('estimate-group-clipboard', [], true),
            clipboardGroupItemData: remoteStorage.getItem('estimate-group-item-clipboard', [], true),
            estimateKey: 1,
            store: {
                materials: [],
                labourRates: [],
                package: Object,
                loaded: false
            },
            scrollOffsetData: [],
            pinging: null,
            contacts: {data: [], loaded: false},
            mode: 'summary',
            multimode: false,
            newAttendance: {
                description: ''
            },
            materialMarkup: '',
        }
    },
    watch: {
        estimate: {
            handler(newValue, oldValue) {
                this.materialMarkup = newValue.materialMarkup + '%'
                this.selectedItem.revoked = newValue.revoked
                this.selectedItem.emailed = newValue.emailed
                this.selectedItem.viewed = newValue.viewed
                this.selectedItem.title = newValue.title
                this.selectedItem.total = newValue.total
                this.selectedItem.summarySubject = newValue.getSummaryParagraph('Subject')
                this.selectedItem.status = newValue.status
                this.selectedItem.buildTokens()
                if (newValue.locked && !this.estimate.readonly) {
                    this.toggleEditMode(true)
                }
                if (this.$refs.attendance && !this.estimate.readonly) {
                    this.$refs.attendance.forEach((i) => {
                        this.$nextTick(() => {
                            i.$refs['autoresizable-comment'].forceResize()
                            i.$refs['autoresizable-description'].forceResize()
                        })
                    })
                }
            },
            deep: true,
        },
        userClipboardToBeWatched: {
            handler(newValue, oldValue) {
                this.clipboardItemData = remoteStorage.getItem('estimate-item-clipboard', [], true)
                this.clipboardTaskData = remoteStorage.getItem('estimate-task-clipboard', [], true)
                this.clipboardGroupData = remoteStorage.getItem('estimate-group-clipboard', [], true)
                this.clipboardGroupItemData = remoteStorage.getItem('estimate-group-item-clipboard', [], true)
                this.clipboardKey++
            },
        },
    },
    computed: {
        userClipboardToBeWatched() {
            return this.user.remoteStorage ? this.user.remoteStorage['estimate-item-clipboard'] +
                this.user.remoteStorage['estimate-section-clipboard'] +
                this.user.remoteStorage['estimate-task-clipboard'] +
                this.user.remoteStorage['estimate-group-clipboard'] +
                this.user.remoteStorage['estimate-group-item-clipboard']
                : ''
        },
    },
    methods: {
        ping(e) {
            this.pinging = e;
        },
        reorder() {
            this.estimate.update();
            this.estimate.addSnapshot();
        },
        addAttendance() {
            this.estimate.attendances.push({
                description: this.newAttendance.description,
                comment: '',
                us: false,
                others: false,
            });
            this.$nextTick(() => {
                this.$refs.attendance[this.$refs.attendance.length - 1].$refs.description.focus()
                this.newAttendance.description = ''
                this.estimate.update()
                this.estimate.addSnapshot(true)
            })
        },
        showSummary() {
            this.mode = 'summary'
        },
        showScopeOfWorks() {
            this.activeSectionIndex = 0;
            this.mode = 'scopeOfWorks'
        },
        showPreliminaries() {
            this.mode = 'preliminaries'
        },
        showDayworks() {
            this.mode = 'dayworks'
        },
        showAttendances() {
            this.mode = 'attendances'
        },
        async toggleEditMode(e) {
            if (!e) {
                if (!this.estimate.locked) {
                    this.estimate = await Estimate.find(this.estimate.id)
                    try {
                        await this.estimate.acquireEditLock(User.connectionId)
                        this.estimate.readonly = false
                    } catch (e) {
                        this.estimate.readonly = true
                    }
                    this.estimateKey++
                }
            } else {
                this.estimate.readonly = true
                setTimeout(async () => {
                    await this.estimate.releaseEditLock()
                    this.estimate = await Estimate.find(this.estimate.id)
                    this.estimate.readonly = true
                    this.estimateKey++
                }, 10)
            }
            this.estimateKey++
        },
        summaryToSelected(e) {
            if (this.estimate.summaryTo !== e.name) {
                this.estimate.summaryTo = e.name
                this.estimate.addSnapshot()
                this.estimate.update()
            }
        },
        updateSectionIndex(e) {
            this.activeSectionIndex = e
            this.userKey++;
        },
        async duplicate() {
            let estimate = await this.estimate.duplicateAsRevision()
            this.$nextTick(() => {
                this.$emit('estimateSelect', estimate.id, true)
            })
        },
        print(url) {
            document.querySelectorAll('.print_iframe').forEach(e => e.remove());
            this.iframe = document.createElement('iframe')
            this.iframe.setAttribute('style', 'display:none')
            this.iframe.setAttribute('class', 'print_iframe')
            this.iframe.src = ""
            this.iframe.name = 'pdf'
            this.iframe.onload = () => {
                this.$nextTick(() => {
                        setTimeout(() => {
                            const pdfFrame = window.frames["pdf"]
                            pdfFrame.focus()
                            pdfFrame.print()
                        }, 100)
                    }
                )
            }
            document.body.appendChild(this.iframe)
            this.iframe.src = url + '?time=' + moment().format('x')
        },
        async updateFromListener(data) {
            if (data.operation === 'updateAggregate') {
                if ((data.aggregate.type === 'Estimate') && (data.aggregate.id === this.estimate.id)) {
                    if (this.estimate.readonly || data.aggregate.data.locked) {
                        await this.estimate.reload()
                        this.estimate.readonly = true
                        this.estimateKey++
                    }
                }
            } else if (data.operation === 'editLockAcquired') {
                if ((data.aggregate.type === 'Estimate') && (data.aggregate.id === this.estimate.id)) {
                    if (data.aggregate.data.lockedByConnectionId !== User.connectionId) {
                        await this.estimate.reload()
                        this.estimate.readonly = true
                        this.estimateKey++
                    }
                }
            } else if (data.operation === 'editLockReleased') {
                if ((data.aggregate.type === 'Estimate') && (data.aggregate.id === this.estimate.id)) {
                    await this.estimate.reload()
                    this.estimate.readonly = true
                    this.estimateKey++
                }
            }
        },
        keyDownListener(e) {
            if ((e.key === 'Control') || (e.key === 'Meta')) {
//                this.multimode = true;
            }
        },
        keyUpListener(e) {
            if ((e.key === 'Control') || (e.key === 'Meta')) {
//                this.multimode = false;
            }
        }
    },
    async mounted() {
        if (this.id) {
            this.estimate = await Estimate.find(this.id)
            this.estimate.readonly = this.estimate.locked
            this.estimate.readonly = true
            User.registerAggregate({'type': 'Estimate', 'id': this.estimate.id}, this.updateFromListener)
            this.materialMarkup = this.estimate.materialMarkup+'%'
        }
        this.$emit('ready')
        await Promise.all([
            Package.find(this.estimate.packageReference),
            MaterialNew.all(),
            EstimateLabourRate.all(),
            Contact.all(),
            Contact.forPackage(this.estimate.packageReference),
            SupplierQuote.all(this.estimate.packageReference),
        ]).then((a) => {
            this.store.package = a[0]
            this.store.materials = a[1]
            this.store.labourRates = a[2]
            this.contacts.packageData = {
                name: a[0].name,
                contacts: a[4],
            }
            this.contacts.data = a[3]
            this.store.supplierQuotes = a[5]
            this.contacts.loaded = true
            this.store.loaded = true
        })
        document.addEventListener('keydown', this.keyDownListener)
        document.addEventListener('keyup', this.keyUpListener)
    },
    beforeDestroy() {
        document.querySelectorAll('.print_iframe').forEach(e => e.remove());
        document.removeEventListener('keydown', this.keyDownListener)
        document.removeEventListener('keyup', this.keyUpListener)
        if (this.estimate.editing && (this.estimate.editingByReference === this.user.id)) {
            this.estimate.releaseEditLock()
        }
    }
}
</script>
